import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/shared/Layout';
import { reportAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  Search, X, Palmtree, ClipboardList,
  CheckCircle2, XCircle, Clock, Ban, FileDown,
} from 'lucide-react';

// ── Excel export ──────────────────────────────────────────────────────────────
const exportToExcel = (data, columns, filename) => {
  // Cabeceras
  const header = columns.map(c => c.label);

  // Filas
  const rows = data.map(row => columns.map(c => c.value(row)));

  // Construir CSV compatible con Excel (UTF-8 BOM para que Excel lo lea bien)
  const BOM = '\uFEFF';
  const sep = ';'; // punto y coma — estándar en Excel latinoamérica
  const csv = BOM + [header, ...rows]
    .map(row => row.map(cell => {
      const val = cell == null ? '' : String(cell);
      // Escapar celdas con comas, punto y coma, saltos o comillas
      return val.includes(sep) || val.includes('"') || val.includes('\n')
        ? `"${val.replace(/"/g, '""')}"`
        : val;
    }).join(sep))
    .join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const safeDate = (d) => {
  try { return format(new Date(d), 'dd/MM/yyyy'); } catch { return '—'; }
};

const statusLabel = (s) => ({
  pending:'Pendiente', approved:'Aprobado',
  rejected:'Rechazado', cancelled:'Cancelado',
}[s] || s);

const statusBadge = (s) => {
  const map = {
    pending:   ['badge-pending',   Clock],
    approved:  ['badge-approved',  CheckCircle2],
    rejected:  ['badge-rejected',  XCircle],
    cancelled: ['badge-cancelled', Ban],
  };
  const [cls, Icon] = map[s] || ['badge-pending', Clock];
  return (
    <span className={`badge ${cls}`} style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
      <Icon size={11} /> {statusLabel(s)}
    </span>
  );
};

const leaveDuration = (r) => {
  if (parseFloat(r.days) === 1)          return 'Día completo';
  if (r.half_day_period === 'morning')   return 'Medio día (mañana)';
  if (r.half_day_period === 'afternoon') return 'Medio día (tarde)';
  return `${r.days}d`;
};

// Columnas para exportar vacaciones
const VAC_COLUMNS = [
  { label: 'Empleado',      value: r => `${r.first_name} ${r.last_name}` },
  { label: 'Departamento',  value: r => r.department || '' },
  { label: 'Desde',         value: r => safeDate(r.start_date) },
  { label: 'Hasta',         value: r => safeDate(r.end_date) },
  { label: 'Días',          value: r => r.total_days },
  { label: 'Estado',        value: r => statusLabel(r.status) },
  { label: 'Notas',         value: r => r.notes || '' },
  { label: 'Fecha solicitud', value: r => safeDate(r.created_at) },
];

// Columnas para exportar permisos
const LEAVE_COLUMNS = [
  { label: 'Empleado',       value: r => `${r.first_name} ${r.last_name}` },
  { label: 'Departamento',   value: r => r.department || '' },
  { label: 'Fecha',          value: r => safeDate(r.request_date) },
  { label: 'Duración',       value: r => leaveDuration(r) },
  { label: 'Estado',         value: r => statusLabel(r.status) },
  { label: 'Notas',         value: r => r.reason || '' },
  { label: 'Fecha solicitud', value: r => safeDate(r.created_at) },
];

// ── Filtros ───────────────────────────────────────────────────────────────────
function Filters({ filters, setFilters, employees, onSearch, loading }) {
  const f = (key) => (e) => setFilters(p => ({ ...p, [key]: e.target.value }));
  const handleReset = () => setFilters({ date_from:'', date_to:'', employee_id:'all', status:'all' });

  return (
    <div className="card section-gap">
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:12, alignItems:'end' }}>
        <div className="form-group" style={{ margin:0 }}>
          <label className="form-label">Desde</label>
          <input type="date" className="form-input" value={filters.date_from} onChange={f('date_from')} />
        </div>
        <div className="form-group" style={{ margin:0 }}>
          <label className="form-label">Hasta</label>
          <input type="date" className="form-input"
            value={filters.date_to} min={filters.date_from || undefined} onChange={f('date_to')} />
        </div>
        <div className="form-group" style={{ margin:0 }}>
          <label className="form-label">Empleado</label>
          <select className="form-input form-select" value={filters.employee_id} onChange={f('employee_id')}>
            <option value="all">Todos los empleados</option>
            {employees.map(e => (
              <option key={e.id} value={e.id}>
                {e.first_name} {e.last_name}{e.department ? ` (${e.department})` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group" style={{ margin:0 }}>
          <label className="form-label">Estado</label>
          <select className="form-input form-select" value={filters.status} onChange={f('status')}>
            <option value="all">Todos los estados</option>
            <option value="pending">Pendiente</option>
            <option value="approved">Aprobado</option>
            <option value="rejected">Rechazado</option>
            <option value="cancelled">Cancelado</option>
          </select>
        </div>
      </div>
      <div style={{ display:'flex', gap:10, marginTop:14, justifyContent:'flex-end' }}>
        <button className="btn btn-secondary" onClick={handleReset}
          style={{ display:'flex', alignItems:'center', gap:6 }}>
          <X size={14} /> Limpiar
        </button>
        <button className="btn btn-primary" onClick={onSearch} disabled={loading}
          style={{ display:'flex', alignItems:'center', gap:6 }}>
          <Search size={14} /> {loading ? 'Buscando…' : 'Buscar'}
        </button>
      </div>
    </div>
  );
}

function SummaryChip({ label, value, color }) {
  return (
    <div style={{
      padding:'8px 14px', borderRadius:8,
      background:'var(--gray-50)', border:'1px solid var(--gray-200)',
      display:'flex', flexDirection:'column', gap:2,
    }}>
      <span style={{ fontSize:11, color:'var(--gray-500)' }}>{label}</span>
      <span style={{ fontSize:18, fontWeight:700, color: color || 'var(--gray-900)' }}>{value}</span>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function ReportPage() {
  const [employees, setEmployees] = useState([]);
  const [vacations, setVacations] = useState(null);
  const [leaves,    setLeaves]    = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [searched,  setSearched]  = useState(false);
  const [activeTab, setActiveTab] = useState('vacations');

  const [filters, setFilters] = useState({
    date_from: '', date_to: '', employee_id: 'all', status: 'all',
  });

  useEffect(() => {
    reportAPI.getEmployees()
      .then(r => setEmployees(r.data))
      .catch(() => toast.error('Error al cargar empleados'));
  }, []);

  const handleSearch = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.date_from)             params.date_from   = filters.date_from;
      if (filters.date_to)               params.date_to     = filters.date_to;
      if (filters.employee_id !== 'all') params.employee_id = filters.employee_id;
      if (filters.status !== 'all')      params.status      = filters.status;

      const [vRes, lRes] = await Promise.all([
        reportAPI.getVacations(params),
        reportAPI.getLeaves(params),
      ]);
      setVacations(vRes.data);
      setLeaves(lRes.data);
      setSearched(true);
    } catch {
      toast.error('Error al generar el informe');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleExport = () => {
    const now = format(new Date(), 'yyyy-MM-dd');
    if (activeTab === 'vacations' && vacations?.length) {
      exportToExcel(vacations, VAC_COLUMNS, `vacaciones_${now}.csv`);
      toast.success(`${vacations.length} registros exportados`);
    } else if (activeTab === 'leaves' && leaves?.length) {
      exportToExcel(leaves, LEAVE_COLUMNS, `permisos_${now}.csv`);
      toast.success(`${leaves.length} registros exportados`);
    } else {
      toast.error('No hay datos para exportar');
    }
  };

  const vacSummary = vacations ? {
    total:    vacations.length,
    approved: vacations.filter(v => v.status === 'approved').length,
    pending:  vacations.filter(v => v.status === 'pending').length,
    rejected: vacations.filter(v => v.status === 'rejected').length,
    cancelled: vacations.filter(v => v.status === 'cancelled').length,
    days:     vacations.filter(v => v.status === 'approved').reduce((s, v) => s + parseInt(v.total_days || 0), 0),
  } : null;

  const leaveSummary = leaves ? {
    total:    leaves.length,
    approved: leaves.filter(l => l.status === 'approved').length,
    pending:  leaves.filter(l => l.status === 'pending').length,
    rejected: leaves.filter(l => l.status === 'rejected').length,
    cancelled: leaves.filter(l => l.status === 'cancelled').length,
  } : null;

  const tabStyle = (t) => ({
    padding:'9px 18px', borderRadius:8, cursor:'pointer',
    fontFamily:'var(--font)', fontWeight:500, fontSize:14, border:'none',
    background: activeTab === t ? 'var(--primary)' : 'var(--gray-100)',
    color:      activeTab === t ? 'white' : 'var(--gray-700)',
    display:'flex', alignItems:'center', gap:7, transition:'all 0.15s',
  });

  const currentData = activeTab === 'vacations' ? vacations : leaves;
  const canExport = searched && currentData && currentData.length > 0;

  return (
    <Layout title="Informes">
      <div className="page-header">
        <div>
          <div className="page-title">Informes</div>
          <div className="page-subtitle">Consulta de vacaciones y permisos de día libre por período y empleado</div>
        </div>
      </div>

      <Filters
        filters={filters} setFilters={setFilters}
        employees={employees} onSearch={handleSearch} loading={loading}
      />

      {!searched && !loading && (
        <div className="card" style={{ textAlign:'center', padding:'48px 20px', color:'var(--gray-400)' }}>
          <Search size={36} style={{ margin:'0 auto 12px', display:'block', opacity:0.4 }} />
          <div style={{ fontSize:15, fontWeight:500, color:'var(--gray-600)' }}>
            Selecciona los filtros y haz clic en Buscar
          </div>
          <div style={{ fontSize:13, marginTop:4 }}>
            Puedes dejar los filtros vacíos para traer todos los registros
          </div>
        </div>
      )}

      {searched && (
        <>
          {/* Tabs + botón exportar */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, flexWrap:'wrap', gap:10 }}>
            <div style={{ display:'flex', gap:8 }}>
              <button style={tabStyle('vacations')} onClick={() => setActiveTab('vacations')}>
                <Palmtree size={15} strokeWidth={1.8} />
                Vacaciones
                {vacSummary && (
                  <span style={{ marginLeft:4, background:'rgba(255,255,255,0.25)', borderRadius:10, padding:'1px 8px', fontSize:12 }}>
                    {vacSummary.total}
                  </span>
                )}
              </button>
              <button style={tabStyle('leaves')} onClick={() => setActiveTab('leaves')}>
                <ClipboardList size={15} strokeWidth={1.8} />
                Permisos
                {leaveSummary && (
                  <span style={{ marginLeft:4, background:'rgba(255,255,255,0.25)', borderRadius:10, padding:'1px 8px', fontSize:12 }}>
                    {leaveSummary.total}
                  </span>
                )}
              </button>
            </div>

            {/* Botón exportar */}
            <button
              className="btn btn-secondary"
              onClick={handleExport}
              disabled={!canExport}
              title={canExport ? 'Descargar como Excel (CSV)' : 'Busca primero para exportar'}
              style={{ display:'flex', alignItems:'center', gap:7 }}
            >
              <FileDown size={16} strokeWidth={1.8} />
              Exportar Excel
            </button>
          </div>

          {/* ── Vacaciones ── */}
          {activeTab === 'vacations' && vacations && (
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                <SummaryChip label="Total" value={vacSummary.total} />
                <SummaryChip label="Aprobadas"  value={vacSummary.approved} color="var(--success)" />
                <SummaryChip label="Pendientes" value={vacSummary.pending}  color="var(--warning)" />
                <SummaryChip label="Rechazadas" value={vacSummary.rejected} color="var(--danger)"  />
                <SummaryChip label="Canceladas" value={vacSummary.cancelled} color="var(--gray-500)"  />
                <SummaryChip label="Días tomados" value={`${vacSummary.days}d`} color="var(--primary)" />
              </div>
              <div className="card">
                {vacations.length === 0 ? (
                  <div className="empty-state">
                    <Palmtree size={36} color="var(--gray-300)" style={{ margin:'0 auto 10px', display:'block' }} />
                    <div className="empty-state-title">Sin resultados</div>
                  </div>
                ) : (
                  <div className="table-wrapper">
                    <table>
                      <thead>
                        <tr>
                          <th>Empleado</th><th>Departamento</th>
                          <th>Desde</th><th>Hasta</th><th>Días</th>
                          <th>Estado</th><th>Notas</th><th>Solicitado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vacations.map(v => (
                          <tr key={v.id}>
                            <td style={{ fontWeight:500, whiteSpace:'nowrap' }}>{v.first_name} {v.last_name}</td>
                            <td style={{ color:'var(--gray-500)', fontSize:13 }}>{v.department || '—'}</td>
                            <td style={{ whiteSpace:'nowrap' }}>
                              {v.start_date.slice(0, 10).split('-').reverse().join('/')}
                            </td>
                            <td style={{ whiteSpace:'nowrap' }}>
                              {v.end_date.slice(0, 10).split('-').reverse().join('/')}
                            </td>
                            <td style={{ textAlign:'center', fontWeight:600 }}>{v.total_days}</td>
                            <td>{statusBadge(v.status)}</td>
                            <td style={{ maxWidth:180, fontSize:13, color:'var(--gray-500)' }} className="truncate">{v.notes || '—'}</td>
                            <td style={{ fontSize:13, color:'var(--gray-500)', whiteSpace:'nowrap' }}>{safeDate(v.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Permisos ── */}
          {activeTab === 'leaves' && leaves && (
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                <SummaryChip label="Total" value={leaveSummary.total} />
                <SummaryChip label="Aprobados"  value={leaveSummary.approved} color="var(--success)" />
                <SummaryChip label="Pendientes" value={leaveSummary.pending}  color="var(--warning)" />
                <SummaryChip label="Rechazados" value={leaveSummary.rejected} color="var(--danger)"  />
                <SummaryChip label="Cancelados" value={leaveSummary.cancelled} color="var(--gray-500)"  />
              </div>
              <div className="card">
                {leaves.length === 0 ? (
                  <div className="empty-state">
                    <ClipboardList size={36} color="var(--gray-300)" style={{ margin:'0 auto 10px', display:'block' }} />
                    <div className="empty-state-title">Sin resultados</div>
                  </div>
                ) : (
                  <div className="table-wrapper">
                    <table>
                      <thead>
                        <tr>
                          <th>Empleado</th><th>Departamento</th>
                          <th>Fecha</th><th>Duración / Turno</th>
                          <th>Estado</th><th>Notas</th><th>Solicitado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leaves.map(l => (
                          <tr key={l.id}>
                            <td style={{ fontWeight:500, whiteSpace:'nowrap' }}>{l.first_name} {l.last_name}</td>
                            <td style={{ color:'var(--gray-500)', fontSize:13 }}>{l.department || '—'}</td>
                            <td style={{ whiteSpace:'nowrap' }}>
                              {l.request_date.slice(0, 10).split('-').reverse().join('/')}
                            </td>
                            <td style={{ fontWeight:500 }}>{leaveDuration(l)}</td>
                            <td>{statusBadge(l.status)}</td>
                            <td style={{ maxWidth:180, fontSize:13, color:'var(--gray-500)' }} className="truncate">{l.reason || '—'}</td>
                            <td style={{ fontSize:13, color:'var(--gray-500)', whiteSpace:'nowrap' }}>{safeDate(l.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </Layout>
  );
}
